import { axios, transformAxiosError } from "../utils/axios.create";
import { RoleType, UserRoleType } from "../domain/rol.interface";

export type RolesPaginatedPayload = {
  search?: string;
  status?: string;
  type?: string;
  subType?: string;
  pageNumber: number;
  pageSize: number;
  sortField?: string;
  sortOrder?: number;
};

export type RolesPaginatedResponse = {
  list: RoleType[];
  totalCount: number;
};

export type RolePayload = {
  name: string;
  status?: string;
  type?: string;
  subType?: string;
  actions?: string[];
  createdBy?: string;
  updatedBy?: string;
};

const findAll = async ({ status }: { status: string }): Promise<RoleType[]> => {
  return axios
    .get(`/roles?status=${status}`)
    .then((res) => {
      const body = res.data.body;
      return (body?.list as RoleType[]) || [];
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const findById = async (id: string): Promise<RoleType> => {
  return axios
    .get(`/roles/${id}`)
    .then((res) => {
      return res.data.body as RoleType;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const findByUserId = async (userId: string): Promise<UserRoleType[]> => {
  return axios
    .get(`/users/${userId}/roles`)
    .then((res) => {
      return res.data.body as UserRoleType[];
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const findAllPaginated = async ({
  search,
  status,
  type,
  subType,
  pageNumber,
  pageSize,
  sortField,
  sortOrder,
}: RolesPaginatedPayload): Promise<RolesPaginatedResponse> => {
  const queryParams = new URLSearchParams({
    pageNumber: String(pageNumber + 1),
    pageSize: String(pageSize),
  });

  if (search) queryParams.append("search", search);
  if (status) queryParams.append("status", status);
  if (type) queryParams.append("type", type);
  if (subType) queryParams.append("subType", subType);
  if (sortField) queryParams.append("sortField", sortField);
  if (sortOrder !== undefined) {
    queryParams.append("sortOrder", sortOrder === 1 ? "asc" : "desc");
  }

  return axios
    .get(`/roles?${queryParams.toString()}`)
    .then((res) => {
      const body = res.data.body;
      return {
        list: (body?.list ?? []) as RoleType[],
        totalCount: (body?.totalCount ?? 0) as number,
      };
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const create = async (data: RolePayload): Promise<RoleType> => {
  return axios
    .post("/roles", data)
    .then((res) => {
      return res.data.body as RoleType;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const update = async (id: string, data: RolePayload): Promise<RoleType> => {
  return axios
    .put(`/roles/${id}`, data)
    .then((res) => {
      return res.data.body as RoleType;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const archive = async (id: string): Promise<void> => {
  return axios
    .patch(`/roles/archive/${id}`)
    .then(() => {
      return;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

export const RolesService = {
  findAll,
  findById,
  findByUserId,
  findAllPaginated,
  create,
  update,
  archive,
};
